import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowForwardRounded,
  BuildRounded,
  SearchRounded,
  LocalShippingOutlined,
  ScheduleRounded,
  PersonOutlineRounded,
  CloseRounded,
} from "@mui/icons-material";
import api from "Service/api/api";
import { WORK_ORDERS } from "Shared/Constants/routes";
import {
  SkeletonFill,
  SkeletonStatus,
  SkeletonText,
} from "Components/Dashboard/Skeleton";
import {
  ActiveWorkRecord,
  formatIssueDate,
  getActiveWork,
  filterActiveWork,
  workStatus,
} from "./activeWorkData";
import styles from "./ActiveWork.module.scss";

const VISIBLE_ROWS = 5;

// Same markup as the loaded body, so the card keeps its height on arrival.
const ActiveWorkSkeleton = () => (
  <div aria-hidden="true">
    <div className={styles.distribution}>
      <SkeletonFill />
    </div>
    <div className={styles.legend}>
      <button type="button" tabIndex={-1} disabled>
        All <SkeletonText width="2ch" />
      </button>
    </div>
    <div className={styles.search}>
      <SearchRounded />
      <input
        tabIndex={-1}
        disabled
        placeholder="Find a vehicle, work order or driver"
      />
    </div>
    <ul className={styles.rows}>
      {Array.from({ length: VISIBLE_ROWS }, (_, index) => (
        <li key={index}>
          <span className={styles.vehicleIcon} />
          <div className={styles.record}>
            <div className={styles.recordTitle}>
              <strong>
                <SkeletonText width="5.5em" />
              </strong>
            </div>
            <strong>
              <SkeletonText width="10em" />
            </strong>
            <div className={styles.metadata}>
              <span>
                <SkeletonText width="18em" />
              </span>
            </div>
          </div>
          <span className={styles.openLink} />
        </li>
      ))}
    </ul>
    <div className={styles.footer}>
      <span>
        <SkeletonText width="14em" />
      </span>
    </div>
  </div>
);

const ActiveWork: React.FC = () => {
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search");
  const startDate = searchParams.get("work_order_from");
  const endDate = searchParams.get("work_order_to");
  const [records, setRecords] = useState<ActiveWorkRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(false);
    setStatus("all");
    api.dashboard
      .getDashboardWorkOrder({
        search,
        start_date: startDate,
        end_date: endDate,
      })
      .then(({ data }) => {
        if (current) setRecords(getActiveWork(data));
      })
      .catch(() => {
        if (current) setError(true);
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [search, startDate, endDate, retry]);
  const groups = useMemo(
    () =>
      Array.from(
        records.reduce((counts, record) => {
          const key = record.status || "unknown";
          counts.set(key, (counts.get(key) || 0) + 1);
          return counts;
        }, new Map<string, number>()),
      ),
    [records],
  );
  const filtered = filterActiveWork(records, status, query);
  const hasFilters = Boolean(search || startDate || endDate);
  return (
    <section className={styles.block} aria-labelledby="active-work-title">
      <header className={styles.header}>
        <div className={styles.title}>
          <BuildRounded aria-hidden="true" />
          <h2 id="active-work-title">Active work</h2>
        </div>
        <Link className={styles.allLink} to={WORK_ORDERS}>
          Work orders <ArrowForwardRounded aria-hidden="true" />
        </Link>
      </header>
      {error ? (
        <div className={styles.message} role="alert">
          <p>Active work could not be loaded.</p>
          <button onClick={() => setRetry((value) => value + 1)}>
            Try again
          </button>
        </div>
      ) : (
        <>
          <div className={styles.summary}>
            <strong>
              {loading ? <SkeletonText width="1.2em" /> : records.length}
            </strong>
            <div>
              <span>active work orders</span>
              <small>
                {hasFilters ? "Current work-order filters" : "All dates"} ·
                oldest first
              </small>
            </div>
            <BuildRounded className={styles.summaryIcon} aria-hidden="true" />
          </div>
          {hasFilters && (
            <p className={styles.filterNote}>
              {search && `Search: ${search}. `}
              {startDate && `From ${startDate}. `}
              {endDate && `Through ${endDate}.`}
            </p>
          )}
          {loading && (
            <>
              <SkeletonStatus>Loading active work…</SkeletonStatus>
              <ActiveWorkSkeleton />
            </>
          )}
          {!loading && records.length > 0 && (
            <>
              <div
                className={styles.distribution}
                aria-label="Filter work orders by status"
              >
                {groups.map(([key, count]) => (
                  <button
                    key={key}
                    type="button"
                    data-status={key}
                    style={{ flexGrow: count }}
                    aria-label={`${workStatus(key)}: ${count} work orders`}
                    aria-pressed={status === key}
                    title={`${workStatus(key)} · ${count}`}
                    onClick={() => setStatus(status === key ? "all" : key)}
                  >
                    <span className={styles.srOnly}>
                      {workStatus(key)}: {count}
                    </span>
                  </button>
                ))}
              </div>
              <div className={styles.legend}>
                <button
                  type="button"
                  aria-pressed={status === "all"}
                  onClick={() => setStatus("all")}
                >
                  All <b>{records.length}</b>
                </button>
                {groups.map(([key, count]) => (
                  <button
                    key={key}
                    type="button"
                    data-status={key}
                    aria-pressed={status === key}
                    onClick={() => setStatus(status === key ? "all" : key)}
                  >
                    <i data-status={key} aria-hidden="true" />
                    {workStatus(key)} <b>{count}</b>
                  </button>
                ))}
              </div>
              <div className={styles.search}>
                <SearchRounded aria-hidden="true" />
                <input
                  aria-label="Search active work"
                  placeholder="Find a vehicle, work order or driver"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
                {query && (
                  <button
                    type="button"
                    aria-label="Clear work search"
                    onClick={() => setQuery("")}
                  >
                    <CloseRounded />
                  </button>
                )}
              </div>
            </>
          )}
          {loading ? null : !records.length ? (
            <p className={styles.message}>
              {hasFilters
                ? "No active work matches these filters."
                : "No active work orders to review."}
            </p>
          ) : !filtered.length ? (
            <p className={styles.message}>
              No active work matches this search and status.
            </p>
          ) : (
            <>
              <ul className={styles.rows}>
                {filtered.slice(0, VISIBLE_ROWS).map((record) => {
                  const reference =
                    record.reference_code != null
                      ? `WO ${record.reference_code}`
                      : "Work order";
                  const driver = [
                    record.driver_first_name,
                    record.driver_last_name,
                  ]
                    .filter(Boolean)
                    .join(" ")
                    .trim();
                  return (
                    <li key={record.id}>
                      <span className={styles.vehicleIcon}>
                        <LocalShippingOutlined aria-hidden="true" />
                      </span>
                      <div className={styles.record}>
                        <div className={styles.recordTitle}>
                          <Link to={`${WORK_ORDERS}/${record.id}`}>
                            {reference}
                          </Link>
                          <span
                            className={styles.status}
                            data-status={record.status || "unknown"}
                          >
                            {workStatus(record.status)}
                          </span>
                        </div>
                        <strong>
                          {record.vehicle_name || "Vehicle not recorded"}
                        </strong>
                        <div className={styles.metadata}>
                          <span>
                            <ScheduleRounded aria-hidden="true" />
                            {formatIssueDate(record.issue_date)}
                          </span>
                          <span>
                            <PersonOutlineRounded aria-hidden="true" />
                            {driver || "Driver not recorded"}
                          </span>
                        </div>
                      </div>
                      <Link
                        className={styles.openLink}
                        to={`${WORK_ORDERS}/${record.id}`}
                        aria-label={`Open ${reference}${record.vehicle_name ? ` for ${record.vehicle_name}` : ""}`}
                      >
                        <ArrowForwardRounded aria-hidden="true" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <div className={styles.footer}>
                <span>
                  {Math.min(VISIBLE_ROWS, filtered.length)} of {filtered.length}{" "}
                  matching work orders
                </span>
                <span>Oldest issued first</span>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};
export default ActiveWork;
